import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { FeatureSet } from '../../models/feature.model';
import { AuthService } from '../../services/auth/auth.service';
import { CasbinPermissionsService } from '../../services/feature/casbin-permissions.service';
import { DccFeatureAccessService } from '../../services/feature/dcc-feature-access.service';

export function dockPermissionGuard(
  requiredAction: 'view' | 'manage',
  resource: FeatureSet = 'package',
): CanActivateFn {
  return async (route, state) => {
    const auth = inject(AuthService);
    const dccFeatureAccessService = inject(DccFeatureAccessService);
    const permissions = inject(CasbinPermissionsService);
    const router = inject(Router);
    const dccCode = route.paramMap.get('dccCode');
    const featureFlag = route.data?.['featureFlag'] as string | undefined;

    const createAccessDeniedUrl = (overrideReason?: string) => {
      const returnTo = state.url.split('/').slice(0, 3).join('/');
      const feature = route.data?.['featureLabel'];
      const policyParams = auth.getPolicyAccessDeniedQueryParams(
        dccCode || undefined,
      );

      return router.createUrlTree([returnTo, 'access-denied'], {
        queryParams: {
          returnTo,
          feature,
          dccCode,
          attemptedRoute: route.routeConfig?.path,
          ...policyParams,
          ...(overrideReason ? { reason: overrideReason } : {}),
        },
      });
    };

    if (auth.isPolicyAccessDenied()) {
      return createAccessDeniedUrl();
    }

    if (
      featureFlag &&
      !(await dccFeatureAccessService.canAccess(featureFlag, dccCode))
    ) {
      return createAccessDeniedUrl('dcc-feature-unavailable');
    }

    const isAdmin = auth.authProfile()?.isAdmin ?? false;
    const hasFullAccess = permissions.hasFullAccess();
    if (isAdmin || hasFullAccess) return true;

    if (!permissions.hasBusinessEntityAccess(dccCode)) {
      return createAccessDeniedUrl('no-permission');
    }

    const hasRequiredPermission =
      permissions.hasPermission(
        resource,
        requiredAction,
        dccCode || undefined,
      ) ||
      (requiredAction === 'view' &&
        permissions.hasPermission(resource, 'manage', dccCode || undefined));

    if (!hasRequiredPermission) {
      return createAccessDeniedUrl('no-permission');
    }

    return true;
  };
}
